import React, { useEffect, useState } from "react";
import { searchFilesByModule } from "../../services/FilesService/FilesService";
import toast from "react-hot-toast";
import SimpleLoader from "../../components/Loader/SimpleLoader";
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  EyeIcon,
} from "@heroicons/react/24/solid";
import { useFilePreview } from "../../hooks/useFilePreview";
import FilePreviewModal from "../../components/FilePreviewModal/FilePreviewModal";
import { downloadFile } from "../../utils/fileUtils";

const Quzhattar = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingFile, setDownloadingFile] = useState(null);

  // Use the reusable file preview hook
  const {
    isPreviewOpen,
    previewUrl,
    isLoading: isPreviewLoading,
    openPreview,
    closePreview,
  } = useFilePreview();

  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const response = await searchFilesByModule({
          module_code: "quzhattar",
        });
        if (response?.files?.length) {
          setFiles(response.files);
        } else {
          toast.error("Құжаттар табылмады.");
        }
      } catch (err) {
        toast.error("Құжаттарды жүктеу кезінде қате орын алды.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, []);

  const handlePreview = async (file) => {
    setSelectedFile(file);
    await openPreview(file.file_url, file.file_name);
  };

  const handleDownload = async (file) => {
    setDownloadingFile(file.uuid);
    try {
      downloadFile(file.file_url, file.file_name);
      toast.success(`"${file.label || file.file_name}" жүктеу басталды!`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Файлды жүктеу кезінде қате орын алды.");
    } finally {
      setDownloadingFile(null);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto text-center">
      <h1 className="text-3xl font-bold text-[#674DA2] mb-8">
        Ағымдағы құжаттар
      </h1>

      {loading ? (
        <div className="flex justify-center items-center py-10">
          <SimpleLoader className="w-10 h-10 text-[#674DA2]" />
        </div>
      ) : files.length === 0 ? (
        <p>Құжаттар табылмады.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {files.map((file) => (
            <div
              key={file.uuid}
              className="bg-white p-5 rounded-xl shadow hover:shadow-lg hover:scale-[1.02] hover:ring-1 hover:ring-[#674DA2] transition flex flex-col"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-[#674DA2]-100 rounded-full">
                  <DocumentTextIcon className="w-10 h-10 text-[#674DA2]" />
                </div>
                <h2 className="font-semibold text-lg text-[#674DA2]">
                  {file.label || file.file_name}
                </h2>
              </div>
              <p className="text-sm text-gray-500 mb-4">
                {file.file_name} ({file.file_size_mb.toFixed(2)} MB)
              </p>

              {/* Action buttons */}
              <div className="flex gap-2 mt-auto">
                {/* Preview button */}
                <button
                  onClick={() => handlePreview(file)}
                  disabled={isPreviewLoading}
                  className="flex-1 bg-[#674DA2] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#5a4190] transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPreviewLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <EyeIcon className="w-5 h-5" />
                  )}
                  Көру
                </button>

                {/* Download button */}
                <button
                  onClick={() => handleDownload(file)}
                  disabled={downloadingFile === file.uuid}
                  className="flex-1 bg-[#674DA2] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#5a4190] transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {downloadingFile === file.uuid ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <ArrowDownTrayIcon className="w-5 h-5" />
                  )}
                  Жүктеу
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* File Preview Modal */}
      <FilePreviewModal
        isOpen={isPreviewOpen}
        previewUrl={previewUrl}
        onClose={closePreview}
        title={
          selectedFile
            ? `${selectedFile.label || selectedFile.file_name}`
            : "Алдын ала қарау"
        }
      />
    </div>
  );
};

export default Quzhattar;
